import React, { createContext, useContext, ReactNode } from 'react';
import { ConfigProvider } from 'antd';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const isDarkMode = false;
  const toggleTheme = () => {
    // No-op since we don't support dark mode switching yet
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#306046', // Primary Brand Color (famousindianrecipes Green)
            colorPrimaryHover: '#234633', // Hover state
            borderRadius: 8,
            colorBgContainer: '#ffffff',
            colorBgElevated: '#ffffff',
            colorBgLayout: '#ffffff',
            colorBorder: '#e2e8f0',
            colorBorderSecondary: '#f1f5f9',
            colorText: '#333333',
            colorTextSecondary: '#666666',
            colorTextTertiary: '#999999',
            colorSuccess: '#059669',
            colorWarning: '#d97225', // Secondary brand warning color
            colorError: '#dc2626',
            colorInfo: '#2563eb',
            fontFamily: '"Inter", "Playfair Display", "Segoe UI", system-ui, sans-serif',
            fontSize: 14,
            fontSizeHeading1: 32,
            fontSizeHeading2: 24,
            fontSizeHeading3: 20,
            fontSizeHeading4: 16,
            fontSizeHeading5: 14,
            fontSizeLG: 16,
            fontSizeSM: 12,
            fontSizeXL: 20,
            lineHeight: 1.6,
            lineHeightHeading1: 1.2,
            lineHeightHeading2: 1.3,
            lineHeightHeading3: 1.4,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            boxShadowSecondary: '0 1px 2px rgba(0, 0, 0, 0.05)',
          },
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
